import { Box, Grid, Paper, Stack, styled, Typography } from "@mui/material";
import TextField from "@mui/material/TextField";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { api } from "api";
import { Curriculum, RoleType, UserResultProps } from "api/types";
import { AssignmentModelList } from "components/AssignmentModelList";
import { CommentList } from "components/CommentList";
import { CurriculumCard } from "components/CurriculumCard";
import { DownloadDialog } from "components/DownloadDialog/DownloadDialog";
import { Assignment, getStatus } from "helpers/assignment";
import { useAuthStatus } from "helpers/useAuthStatus";
import { useBreadcrumbs } from "helpers/useBreadcrumbs";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { DateAsYYYY_MM_DD } from "../../../api/base";
// import { useParams } from "react-router-dom";
import { InfoSnackBar } from "../../../components/InfoSnackBar";
import { GET_RESULT_FAIL } from "../../../messages";

const defaultUserResultProps: UserResultProps = {
	date: "",
	name: "",
	yearLevel: "",
	totalAssignment: "",
	totalCorrect: "",
	totalAnswered: "",
	assignmentResults: [],
	curriculumResults: [],
	capabilityGrades: [],
	topicGrades: [],
	comments: [],
};

const PREFIX = "IndivResultPage";
const classes = {
	root: `${PREFIX}-root`,
	button: `${PREFIX}-button`,
	title: `${PREFIX}-title`,
	groupList: `${PREFIX}-groupList`,
	summaryCard: `${PREFIX}-summaryCard`,
	summaryTitle: `${PREFIX}-summaryTitle`,
	summaryText: `${PREFIX}-summaryText`,
	icon: `${PREFIX}-icon`,
	bluePaper: `${PREFIX}-bluePaper`,
	Card: `${PREFIX}-Card`,
};

const Root = styled("div")(({ theme }) => ({
	[`&.${classes.root}`]: {
		padding: 20,
	},
	[`& .${classes.summaryCard}`]: {
		marginTop: 30,
		Height: 168,
		padding: theme.spacing(10),
		Width: 1200,
		marginRight: theme.spacing(4),
	},
	[`& .${classes.Card}`]: {
		marginTop: 30,
		Height: 100,
		padding: theme.spacing(10),
		Width: 1200,
		marginRight: theme.spacing(4),
	},
	[`& .${classes.bluePaper}`]: {
		marginTop: 30,
		minHeight: 80,
		padding: theme.spacing(2),
		minWidth: 200,
		marginRight: theme.spacing(4),
		// backgroundColor: blue[900]
		backgroundColor: "#09203F",
	},
}));

export const IndivResultPage = (props: {
	setLoading: any;
	uuid: string;
}): JSX.Element => {
	const { setLoading, uuid } = props;
	const { role } = useAuthStatus();
	const [curriculums, setCurriculum] = useState<Curriculum[]>([]);
	const [assignments, setAssignment] = useState<Assignment[]>([]);
	const { updateLastBreadcrumbs } = useBreadcrumbs();
	const [err, setErr] = useState("");
	const [yearLevel, setYearLevel] = useState("");
	const [totalAssignment, setTotalAssignment] = useState("");
	const [btnLoading, setBtnLoading] = useState(true);
	const [docTitle, setDocTitle] = useState("");
	// const [setTotalCorrect] = useState("");
	const [totalAnswered, setTotalAnswered] = useState("");
	const [userResultProps, setUserResultProps] = useState<UserResultProps>(
		defaultUserResultProps,
	);
	const [fromDate, setFromDate] = React.useState<Date | null>(null);
	const componentMounted = useRef(true);

	const fetchResult = useCallback(() => {
		// no userId given
		// need to retrieve it via /people/me first
		if (uuid !== "") {
			api
				.getIndividualResult(
					uuid,
					fromDate === null ? undefined : DateAsYYYY_MM_DD(fromDate),
				)
				.then((res) => {
					if (res.status !== 200) {
						throw new Error();
					}
					setErr("");
					setDocTitle(
						res.data.firstName + " " + res.data.lastName + "'s Academic Result",
					);
					// setName(res.data.firstName + " " + res.data.lastName);
					setYearLevel(String(res.data.yearLevel));
					setTotalAssignment(String(res.data.totalAssignment));
					// setTotalCorrect(String(res.data.totalCorrect));
					setTotalAnswered(String(res.data.totalAnswered));
					// setAssignmentResults(res.data.assignmentResultList);
					if (res.data.curriculumResultList !== null) {
						setCurriculum(
							res.data.curriculumResultList.map((a) => a.curriculumDetails),
						);
					} else {
						setCurriculum([]);
					}

					setUserResultProps({
						date: res.data.date,
						name: res.data.firstName + " " + res.data.lastName,
						yearLevel: String(res.data.yearLevel),
						totalAssignment: String(res.data.totalAssignment),
						totalCorrect: String(res.data.totalCorrect),
						totalAnswered: String(res.data.totalAnswered),
						assignmentResults: res.data.assignmentResultList,
						curriculumResults: res.data.curriculumResultList,
						capabilityGrades: res.data.capabilityGradeList,
						topicGrades: res.data.topicGradeList,
						comments: res.data.commentList !== null ? res.data.commentList : [],
					});
					setLoading(false);
					setBtnLoading(false);
					if (res.data) {
						updateLastBreadcrumbs({
							name:
								"Result for " + res.data.firstName + " " + res.data.lastName,
						});
					}
				})
				.catch(() => {
					setErr(GET_RESULT_FAIL);
				});
		}
	}, [uuid, fromDate, setLoading, updateLastBreadcrumbs]);

	useEffect(() => {
		fetchResult();
	}, [fetchResult]);

	useEffect(() => {
		api
			.getMyUserData()
			.then((res) => {
				if (res.status !== 200) {
					throw new Error();
				}

				return res.data;
			})
			.then((res) => {
				if (
					res.assignments &&
					res.assignments.length &&
					res.assignments[0]?.groupId !== undefined
				) {
					api
						.getGroupAssignments(res.assignments[0]?.groupId)
						.then((resdata) => {
							if (resdata.status !== 200) {
								throw new Error();
							}
							const now = new Date();
							setAssignment(
								(resdata.data || []).map((ass) => ({
									...ass,
									status: getStatus(ass, now),
								})),
							);
							setLoading(false);
						})
						.catch(() => {
							setLoading(false);
						});
				}
			})
			.catch(() => {});
		return () => {
			componentMounted.current = false;
		};

		// eslint-disable-next-line
	}, []);

	const now = new Date();
	// const Date_String = now.toLocaleDateString();
	const formattedDate = now
		.toLocaleDateString("en-GB", {
			day: "numeric",
			month: "numeric",
			year: "numeric",
		})
		.replace(/ /g, "-");

	return (
		<Root className={classes.root}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<InfoSnackBar
						isOpen={err !== ""}
						severity="error"
						onClose={() => setErr("")}
						message={err}
					/>
				</Grid>
				<Grid item xs={3}>
					<LocalizationProvider dateAdapter={AdapterDateFns}>
						<DatePicker
							label="From"
							value={fromDate}
							onChange={(newValue) => {
								setFromDate(newValue);
							}}
							renderInput={(params) => <TextField {...params} />}
						/>
					</LocalizationProvider>
				</Grid>

				<Grid item xs={12}>
					<Paper className={classes.Card}>
						<Stack direction="row" spacing={10}>
							<Grid item xs={7}>
								<Stack direction="row" spacing={10}>
									<Typography variant="h4">Year Level: {yearLevel}</Typography>
									<Typography variant="h4">
										Assigned: {totalAssignment}
									</Typography>
									<Typography variant="h4">
										Completed: {totalAnswered}
									</Typography>
								</Stack>
							</Grid>
							<Grid item xs={3}>
								<Typography variant="h4">
									Report Date: {formattedDate}
								</Typography>
							</Grid>

							<DownloadDialog
								role={role as RoleType}
								loading={btnLoading}
								baseName={docTitle}
								resultToBeRendered={userResultProps}
								templateType={"individual"}
							/>
						</Stack>
					</Paper>
				</Grid>
				<Grid item xs={12}>
					<Typography variant="h3" data-testid="hello">
						Recent Assignments
					</Typography>
				</Grid>
				{/* <Grid item xs={12}>
					<Paper className={classes.summaryCard}>

					</Paper>
					</Grid> */}
				<Grid item xs={12}>
					{assignments.length === 0 ? (
						<Typography variant="h5" className={classes.title}>
							No ongoing assignments
						</Typography>
					) : (
						<AssignmentModelList assignments={assignments} show_csv={false} />
					)}
				</Grid>
				<Grid item xs={12}>
					<Typography variant="h3" data-testid="hello">
						Curriculums
					</Typography>
				</Grid>
				<Grid item xs={12}>
					<Paper className={classes.summaryCard}>
						<Box
							display="grid"
							gap={2}
							gridTemplateColumns="repeat(auto-fit, minmax(300px, 1fr))"
						>
							{curriculums.length === 0 ? (
								<Typography variant="h4">No ongoing curriculums</Typography>
							) : (
								curriculums.map((item) => (
									<Box key={`${item.id} ${item.code} ${item.name}`}>
										<CurriculumCard curriculum={item} />
									</Box>
								))
							)}
						</Box>
					</Paper>
				</Grid>
				<Grid item xs={12}>
					<Typography variant="h3" data-testid="hello">
						Comments
					</Typography>
					<CommentList commentList={userResultProps.comments} />
				</Grid>
			</Grid>
		</Root>
	);
};
